import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ADVISOR_NAV } from "@/pages/advisor/AdvisorHome";
import { InterestResults } from "@/components/dashboard/InterestResults";
import { downloadInterestPdf } from "@/lib/pdf";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Compass, Lock, AlertTriangle, FileDown } from "lucide-react";

// Advisor / faculty read-only view of a student's shared O*NET interest profile.
// Server-side authorization runs on every request; revoked links return 404.
export default function AdvisorInterestView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/assessments/shared/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "This shared link is no longer available."));
  }, [token]);

  return (
    <DashboardShell title="Shared Career Interests" subtitle="Read-only · shared by a student" nav={ADVISOR_NAV}>
      {!data && !err && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {err && (
        <Card className="mx-auto max-w-2xl p-8 text-center" data-testid="shared-error">
          <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
          <p className="mt-3 font-heading text-lg font-bold text-[#003057]">Link unavailable</p>
          <p className="mt-1 text-sm text-slate-500">{err}</p>
        </Card>
      )}
      {data && (
        <div className="mx-auto max-w-4xl space-y-6">
          <Card className="flex flex-wrap items-center justify-between gap-3 border-[#005291]/30 bg-[#005291]/5 p-5" data-testid="shared-header">
            <div>
              <p className="flex items-center gap-2 font-heading text-base font-bold text-[#003057]">
                <Compass className="h-5 w-5 text-[#005291]" /> {data.student_name || "Student"}'s Career Interests
              </p>
              <p className="mt-0.5 text-xs text-slate-500">O*NET Interest Profiler · completed {data.created_at?.slice(0, 10)}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-500">
              <Lock className="h-3.5 w-3.5" /> Read-only
            </span>
            <Button variant="outline" onClick={() => downloadInterestPdf(data, data.student_name)} className="rounded-full font-semibold" data-testid="shared-pdf-btn">
              <FileDown className="mr-2 h-4 w-4" /> PDF
            </Button>
          </Card>
          <InterestResults result={data} />
        </div>
      )}
    </DashboardShell>
  );
}
